'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface CTASectionProps {
  onOrderNow: () => void;
}

const CTASection = ({ onOrderNow }: CTASectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [timeLeft, setTimeLeft] = useState({
    hours: 24,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        } else if (prev.hours > 0) {
          return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
        }
        return { hours: 24, minutes: 0, seconds: 0 };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isHydrated]);

  return (
    <section className="bg-gradient-to-br from-primary via-primary/90 to-secondary py-16 lg:py-24">
      <div className="mx-auto max-w-[1200px] px-6">
        <div className="text-center">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-warning px-4 py-2">
            <Icon name="ClockIcon" size={20} className="text-warning-foreground" />
            <span className="font-cta text-sm font-bold text-warning-foreground">
              LIMITED TIME OFFER
            </span>
          </div>

          <h2 className="font-heading mb-4 text-3xl font-bold text-primary-foreground lg:text-5xl">
            Order Risk-Free Today!
          </h2>
          <p className="mx-auto mb-8 max-w-2xl text-lg text-primary-foreground/90 lg:text-xl">
            Get 20% off on your first order + Free shipping on orders above ₹500
          </p>

          {isHydrated && (
            <div className="mb-8 flex justify-center gap-4">
              <div className="rounded-xl bg-primary-foreground/20 p-4 backdrop-blur-sm">
                <div className="font-heading text-3xl font-bold text-primary-foreground">
                  {String(timeLeft.hours).padStart(2, '0')}
                </div>
                <div className="text-xs text-primary-foreground/80">Hours</div>
              </div>
              <div className="rounded-xl bg-primary-foreground/20 p-4 backdrop-blur-sm">
                <div className="font-heading text-3xl font-bold text-primary-foreground">
                  {String(timeLeft.minutes).padStart(2, '0')}
                </div>
                <div className="text-xs text-primary-foreground/80">Minutes</div>
              </div>
              <div className="rounded-xl bg-primary-foreground/20 p-4 backdrop-blur-sm">
                <div className="font-heading text-3xl font-bold text-primary-foreground">
                  {String(timeLeft.seconds).padStart(2, '0')}
                </div>
                <div className="text-xs text-primary-foreground/80">Seconds</div>
              </div>
            </div>
          )}

          <button
            onClick={onOrderNow}
            className="group mb-8 inline-flex items-center gap-3 rounded-lg bg-background px-10 py-5 font-cta text-xl font-bold text-primary shadow-cta transition-all duration-250 ease-out hover:scale-105 hover:shadow-lg"
          >
            Order Fresh Today
            <Icon
              name="ArrowRightIcon"
              size={24}
              className="transition-transform duration-250 ease-out group-hover:translate-x-1"
            />
          </button>

          <div className="flex flex-wrap items-center justify-center gap-6 text-primary-foreground/90">
            <div className="flex items-center gap-2">
              <Icon name="ShieldCheckIcon" size={24} />
              <span className="text-sm font-medium">100% Money-Back Guarantee</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="TruckIcon" size={24} />
              <span className="text-sm font-medium">Free Shipping Above ₹500</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="CheckBadgeIcon" size={24} />
              <span className="text-sm font-medium">FSSAI Certified</span>
            </div>
          </div>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-3">
          <div className="rounded-xl bg-primary-foreground/10 p-6 text-center backdrop-blur-sm">
            <Icon
              name="HeartIcon"
              size={32}
              className="mx-auto mb-3 text-primary-foreground"
            />
            <h4 className="mb-2 font-bold text-primary-foreground">
              Seasonal Products
            </h4>
            <p className="text-sm text-primary-foreground/80">
              Limited availability - order before stock runs out
            </p>
          </div>
          <div className="rounded-xl bg-primary-foreground/10 p-6 text-center backdrop-blur-sm">
            <Icon
              name="GiftIcon"
              size={32}
              className="mx-auto mb-3 text-primary-foreground"
            />
            <h4 className="mb-2 font-bold text-primary-foreground">
              Perfect for Gifting
            </h4>
            <p className="text-sm text-primary-foreground/80">
              Premium packaging available for all occasions
            </p>
          </div>
          <div className="rounded-xl bg-primary-foreground/10 p-6 text-center backdrop-blur-sm">
            <Icon
              name="SparklesIcon"
              size={32}
              className="mx-auto mb-3 text-primary-foreground"
            />
            <h4 className="mb-2 font-bold text-primary-foreground">
              Fresh Batches
            </h4>
            <p className="text-sm text-primary-foreground/80">
              Made to order - delivered fresh to your doorstep
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;